import { DropResult } from 'react-beautiful-dnd';
import { toast } from 'react-toastify';
import { Container } from '../../../../types/canvas.types';

export interface UseContainerDragDropReturn {
  handleOnDragEnd: (result: DropResult) => void;
  handleNestedItemDragEnd: (result: DropResult, nestedContainerId: string) => void;
}

export const useContainerDragDrop = (
  selectedContainerId: string | null,
  selectedContainer: Container | null,
  activePageId: string | null,
  setContainers: React.Dispatch<React.SetStateAction<Record<string, Container[]>>>
): UseContainerDragDropReturn => {
  const handleOnDragEnd = (result: DropResult) => {
    if (!result.destination || !selectedContainer || !activePageId) {
      return;
    }
    const { source, destination, type } = result;

    if (type === 'component') {
      if (source.index === destination.index) {
        return;
      }

      setContainers(prevContainers => {
        if (!prevContainers[activePageId]) return prevContainers;

        const updateContainerItems = (containers: Container[]): Container[] => {
          return containers.map(container => {
            if (container.id === selectedContainerId) {
              const items = [...(container.content.items || [])];
              const [reorderedItem] = items.splice(source.index, 1);
              items.splice(destination.index, 0, reorderedItem);

              return {
                ...container,
                content: {
                  ...container.content,
                  items
                }
              };
            }

            if (container.content.nestedContainers?.length) {
              return {
                ...container,
                content: {
                  ...container.content,
                  nestedContainers: updateContainerItems(container.content.nestedContainers)
                }
              };
            }

            return container;
          });
        };

        return {
          ...prevContainers,
          [activePageId]: updateContainerItems(prevContainers[activePageId])
        };
      });

      toast.success('Component reordered successfully');
    }

    if (type === 'nested-container') {
      if (source.index === destination.index) {
        return;
      }

      setContainers(prevContainers => {
        if (!prevContainers[activePageId]) return prevContainers;

        let operationSucceeded = true;

        const updateNestedContainers = (containers: Container[]): Container[] => {
          return containers.map(container => {
            if (container.id === selectedContainerId) {
              const nestedContainers = [...(container.content.nestedContainers || [])];

              // Check compatibility before allowing the drop
              const draggedContainer = nestedContainers[source.index];
              const draggedWidth = parseInt(draggedContainer.configuration.width as string) || 100;
              const isFullWidth = draggedWidth === 100;

              // Find what row the destination would be in
              let currentRow = 0;
              let currentRowWidth = 0;
              let destinationRow = 0;

              for (let i = 0; i < destination.index; i++) {
                const containerAtIndex = i < source.index ? nestedContainers[i] : nestedContainers[i + 1];
                const width = parseInt(containerAtIndex.configuration.width as string) || 100;

                if (currentRowWidth + width > 100 && currentRowWidth > 0) {
                  currentRow++;
                  currentRowWidth = 0;
                }
                currentRowWidth += width;
                destinationRow = currentRow;
              }

              // Check if there would be other containers in the same row
              const wouldHaveRowMates = currentRowWidth > 0 ||
                (destination.index < nestedContainers.length - 1 &&
                  currentRowWidth + draggedWidth < 100);

              // Prevent drop if full-width container would be placed with partial-width containers
              if (isFullWidth && wouldHaveRowMates) {
                toast.error('Full-width containers cannot be placed in the same row as other containers');
                operationSucceeded = false;
                return container;
              }

              // Prevent drop if partial-width container would force full-width container to share row
              if (!isFullWidth && currentRowWidth + draggedWidth <= 100) {
                // Check if the next container after destination would be full-width
                let nextIndex = destination.index;
                if (source.index < destination.index) nextIndex++;

                if (nextIndex < nestedContainers.length) {
                  const nextContainer = nestedContainers[nextIndex];
                  const nextWidth = parseInt(nextContainer.configuration.width as string) || 100;

                  if (nextWidth === 100 && currentRowWidth + draggedWidth > 0) {
                    toast.error('Cannot place containers next to full-width containers');
                    operationSucceeded = false;
                    return container;
                  }
                }
              }

              const [reorderedContainer] = nestedContainers.splice(source.index, 1);
              nestedContainers.splice(destination.index, 0, reorderedContainer);

              // Recalculate rowIndex based on new positions and widths
              currentRow = 0;
              currentRowWidth = 0;

              const reorderedWithOrderAndRow = nestedContainers.map((nc, index) => {
                const width = parseInt(nc.configuration.width as string) || 100;

                // If adding this container would exceed 100% width, move to next row
                if (currentRowWidth + width > 100 && currentRowWidth > 0) {
                  currentRow++;
                  currentRowWidth = 0;
                }

                const updatedContainer: Container = {
                  ...nc,
                  order: index,
                  metadata: {
                    ...nc.metadata,
                    rowIndex: currentRow,
                    type: nc.metadata?.type || 'nested-container'
                  }
                };

                currentRowWidth += width;

                return updatedContainer;
              });

              return {
                ...container,
                content: {
                  ...container.content,
                  nestedContainers: reorderedWithOrderAndRow
                }
              };
            }

            if (container.content.nestedContainers?.length) {
              return {
                ...container,
                content: {
                  ...container.content,
                  nestedContainers: updateNestedContainers(container.content.nestedContainers)
                }
              };
            }

            return container;
          });
        };

        const result = {
          ...prevContainers,
          [activePageId]: updateNestedContainers(prevContainers[activePageId])
        };

        // Only show success message if operation actually succeeded
        if (operationSucceeded) {
          toast.success('Container reordered successfully');
        }

        return result;
      });
    }
  };

  const handleNestedItemDragEnd = (result: DropResult, nestedContainerId: string) => {
    if (!result.destination || !activePageId) {
      return;
    }

    const { source, destination } = result;

    if (source.index === destination.index) {
      return;
    }

    setContainers(prevContainers => {
      if (!prevContainers[activePageId]) return prevContainers;

      const updateNestedContainerItems = (containers: Container[]): Container[] => {
        return containers.map(container => {
          if (container.id === nestedContainerId) {
            const items = [...(container.content.items || [])];
            const [reorderedItem] = items.splice(source.index, 1);
            items.splice(destination.index, 0, reorderedItem);

            return {
              ...container,
              content: {
                ...container.content,
                items
              }
            };
          }

          if (container.content.nestedContainers?.length) {
            return {
              ...container,
              content: {
                ...container.content,
                nestedContainers: updateNestedContainerItems(container.content.nestedContainers)
              }
            };
          }

          return container;
        });
      };

      return {
        ...prevContainers,
        [activePageId]: updateNestedContainerItems(prevContainers[activePageId])
      };
    });
  };

  return {
    handleOnDragEnd,
    handleNestedItemDragEnd
  };
};
